import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Progress, Table, Tooltip } from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import {
  CheckCircleFilledIcon,
  CloseCircleFilledIcon,
  InfoCircleFilledIcon,
  ErrorTriangleIcon,
} from 'tdesign-icons-react';
import { throttle } from 'lodash';
import styled from 'styled-components';
import { connect } from 'react-redux';
import useTable from '@/hooks/useTable';
import { sleep } from '@/utils/common';
import { disasterRecoveryClusterCheck, fetchRecoveryClusterCheckInfo } from '@/slices/clusterSlice';
import PartitonError from './PartitonError';
import PermissionsError from './PermissionsError';
import ReplicasError from './ReplicasError';

export const renderYesNo = ({ row, col, primaryClusterName, recoveryClusterName }) => {
  const key = col.colKey;
  const item = row[key];
  const exists = row.existed;
  let errorReason;
  if (!exists) {
    return (
      <Tooltip content='Topic不存在'>
        <CloseCircleFilledIcon color='var(--td-error-color)' />
      </Tooltip>
    );
  }
  if (item) {
    return <CheckCircleFilledIcon color='green' />;
  }

  switch (key) {
    case 'consistentPartitions':
      errorReason = (
        <PartitonError
          topic={row.topicName}
          info={row.partitionDetail}
          primaryClusterName={primaryClusterName}
          recoveryClusterName={recoveryClusterName}
        />
      );
      break;

    case 'consistentReplicas':
      errorReason = (
        <ReplicasError
          topic={row.topicName}
          info={row.replicasDetail}
          primaryClusterName={primaryClusterName}
          recoveryClusterName={recoveryClusterName}
        />
      );
      break;

    case 'consistentPermissions':
      errorReason = (
        <PermissionsError
          topic={row.topicName}
          info={row.permissionsDetail}
          primaryClusterName={primaryClusterName}
          recoveryClusterName={recoveryClusterName}
        />
      );
      break;
    default:
      break;
  }

  return (
    <Tooltip theme='light' overlayInnerStyle={{ maxWidth: 900 }} content={errorReason}>
      <InfoCircleFilledIcon color='var(--td-warning-color)' />
    </Tooltip>
  );
};

function TopicCheck({
  recoveryClusterName,
  primaryClusterName,
  disasterRecoveryClusterCheck,
  fetchRecoveryClusterCheckInfo,
  bindRecovery,
}) {
  const { checking, topics, topicNum } = bindRecovery;
  const interVal = useRef(null);

  const [fakePercent, setFakePercent] = useState(0);

  const scrollToElement = throttle(
    async () => {
      await sleep(200);
      document
        .querySelector('.topic-check-table .t-table__body')
        ?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    },
    1000,
    { trailing: true },
  );

  const columns = [
    {
      title: 'Topic',
      colKey: 'topicName',
      cell: ({ row }) => {
        return (
          <TopicCell>
            {row.missPartitions && (
              <Tooltip content='当前topic在主集群存在partition缺失的情况，将会在下一步处理中尝试修复'>
                <ErrorTriangleIcon color='var(--td-error-color)' />
              </Tooltip>
            )}
            <div className='cell-body'>{row.topicName}</div>
          </TopicCell>
        );
      },
    },
    {
      title: '是否存在',
      colKey: 'existed',
      width: 120,
      cell: renderYesNo,
    },
    {
      title: '分区一致',
      colKey: 'consistentPartitions',
      width: 120,
      cell: (arg1) => renderYesNo({ ...arg1, primaryClusterName, recoveryClusterName }),
    },
    {
      title: '副本一致',
      colKey: 'consistentReplicas',
      width: 120,
      cell: (arg1) => renderYesNo({ ...arg1, primaryClusterName, recoveryClusterName }),
    },
    {
      title: '权限一致',
      colKey: 'consistentPermissions',
      width: 120,
      cell: (arg1) => renderYesNo({ ...arg1, primaryClusterName, recoveryClusterName }),
    },
  ];

  const { config } = useTable({
    columns,
    largeData: true,
  });

  const readyFakePercent = useMemoizedFn(() => {
    setFakePercent(0);
    interVal.current = setInterval(() => {
      setFakePercent((p) => {
        if (p >= 100) {
          clearInterval(interVal.current);
          return 100;
        }
        return p + 1;
      });
    }, 300);
  });

  const loadCheckInfo = useMemoizedFn(async () => {
    if (!checking) {
      return;
    }
    await fetchRecoveryClusterCheckInfo(primaryClusterName);
    await scrollToElement();
    await sleep(500);
    loadCheckInfo();
  });

  // 开始检查
  const startCheck = useMemoizedFn(async () => {
    // 开始检查
    disasterRecoveryClusterCheck({
      recoveryClusterName,
      primaryClusterName,
    });
    readyFakePercent();
    await sleep(1000);
    loadCheckInfo();
  });

  useEffect(() => {
    startCheck();
    return () => {
      clearInterval(interVal.current);
    };
  }, [startCheck]);

  const percent = useMemo(() => {
    const topicLength = topics.length;
    const realPercent = (topicLength / topicNum || 0) * 100;

    if (topicLength > 0) {
      return Math.round(20 + 0.8 * realPercent);
    }

    return Math.round(0.2 * fakePercent);
  }, [topics, topicNum, fakePercent]);

  return (
    <>
      <Header>
        处理进度：
        <Progress
          theme='plump'
          percentage={percent}
          strokeWidth={12}
          style={{ flex: 1, margin: '0 20px 0 10px' }}
        />
        {topics.length > 0 ? `${topics.length}/${topicNum}` : '正在准备...'}
      </Header>
      <Table
        className='topic-check-table'
        {...config}
        rowKey='topicName'
        rowClassName='fadeIn'
        data={topics}
        scroll={{ type: 'scroll', threshold: 1 }}
      />
    </>
  );
}

export default connect((state) => ({ bindRecovery: state.cluster.bindRecovery }), {
  disasterRecoveryClusterCheck,
  fetchRecoveryClusterCheckInfo,
})(TopicCheck);

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 0 20px 0 15px;
  margin-bottom: 28px;
`;

const TopicCell = styled.div`
  display: flex;
  gap: 4px;
  .t-icon {
    flex-shrink: 0;
    height: 25px;
  }
  .cell-body {
    flex: 1;
    width: 0;
  }
`;
